import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import {
  selectShiftPostV2CopyEditInitialValues,
  selectShiftPostV2InitialValues,
} from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { IPositionNameSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/position-name-section.types'
import AuthService from '@src/helpers/authService'
import { NotificationManager } from '@src/helpers/NotificationsManager'
import { handleErrorNotification } from '@src/helpers/ErrorHandlingService/ErrorHandlingService'
import { IDropdownData } from '@src/models/Common'
import { API } from '@src/network'
import { FormikProps } from 'formik'
import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'

export const usePosition = (
  formik: FormikProps<IPositionNameSectionFormValues>,
  sourcePage: ShiftPostPage,
) => {
  const { values, setFieldValue } = formik

  const copyEditInitialValues = useSelector(
    selectShiftPostV2CopyEditInitialValues,
  )
  const postInitialValues = useSelector(selectShiftPostV2InitialValues)

  const businessEntity = AuthService.getBusinessEntity()

  const initialValues =
    sourcePage === ShiftPostPage.POST_SHIFT
      ? postInitialValues
      : copyEditInitialValues

  const [selectedPosition, setSelectedPosition] = useState<IDropdownData>()
  const [defaultSelectedPosition, setDefaultSelectedPosition] = useState<
    IDropdownData
  >()
  const [positionOptions, setPositionOptions] = useState<IDropdownData[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const resetPosition = () => {
    setSelectedPosition(defaultSelectedPosition)
  }

  /**
   * Fetches the position options and sets the preselected option
   */
  const getPositionOptions = useCallback(async () => {
    try {
      const response = await API.GigTypes.getGigTypes({ paginate: false })
      const defaultPositionOptions = response.data.map(value => ({
        value: value.id,
        label: value.name,
        action: value.id,
      }))
      const defaultSelectedPosition = defaultPositionOptions.find(
        position => position.value === values.gig_type_id,
      )

      if (defaultSelectedPosition) {
        setSelectedPosition(defaultSelectedPosition)
        setDefaultSelectedPosition(defaultSelectedPosition)
      }
      setPositionOptions(defaultPositionOptions)
    } catch {}
  }, [values.gig_type_id])

  useEffect(() => {
    getPositionOptions()
  }, [getPositionOptions])

  /**
   * Handles the position change event
   */
  const handlePositionChange = (selectedOption: IDropdownData) => {
    setSelectedPosition(selectedOption)
  }

  /**
   * Handles the position creation event
   */
  const handleCreatePosition = async (value: string) => {
    setIsLoading(true)
    try {
      const response = await API.GigTypes.postGigType({ name: value })

      NotificationManager.success('Proposed new job type', 'Job Type')

      const newPosition = {
        value: response.data.id,
        label: response.data.name,
        action: response.data.id,
      }

      setSelectedPosition(newPosition)
      setPositionOptions([...positionOptions, newPosition])
    } catch (error) {
      handleErrorNotification(error, 'Job Type Error', true)
      return error
    } finally {
      setIsLoading(false)
    }
  }

  const setPositionInFormik = () => {
    setFieldValue('gig_type_id', selectedPosition?.value)
    setFieldValue('gig_type', selectedPosition?.label)

    if (
      selectedPosition?.value !== initialValues?.gig_type_id &&
      businessEntity?.gig_identifier_policy?.when_not_copy &&
      values.gig_identifier === initialValues?.gig_identifier
    ) {
      setFieldValue('gig_identifier', '', true)
    }
  }

  return {
    isLoading,
    handlePositionChange,
    handleCreatePosition,
    positionOptions,
    selectedPosition,
    resetPosition,
    setPositionInFormik,
  }
}
